import type {
  MapDocument,
  MapDoor,
  MapRect,
  MapSegment,
  MapSpace,
} from "./clinicMapClient";

/** Mirrors brian `clinic_map/schemas.py` `GRID_MAX`. */
export const GRID_MAX = 200;

export interface GridPoint {
  x: number;
  y: number;
}

const right = (r: MapRect) => r.x + r.w;
const bottom = (r: MapRect) => r.y + r.h;

const horizontal = (s: MapSegment) => s.y1 === s.y2;

type Touch = "none" | "partial" | "full";

/** Mirrors brian `map_rules._touch`: how much of `segment` lies on `rect`'s outline. */
const touch = (rect: MapRect, segment: MapSegment): Touch => {
  const [line, start, end, edges, low, high] = horizontal(segment)
    ? [
        segment.y1,
        segment.x1,
        segment.x2,
        [rect.y, bottom(rect)],
        rect.x,
        right(rect),
      ]
    : [
        segment.x1,
        segment.y1,
        segment.y2,
        [rect.x, right(rect)],
        rect.y,
        bottom(rect),
      ];
  if (!edges.includes(line) || Math.min(end, high) - Math.max(start, low) <= 0)
    return "none";
  return low <= start && end <= high ? "full" : "partial";
};

const overlap = (a: MapRect, b: MapRect) =>
  Math.min(right(a), right(b)) > Math.max(a.x, b.x) &&
  Math.min(bottom(a), bottom(b)) > Math.max(a.y, b.y);

/** Mirrors brian `map_rules._share_edge`. */
const shareEdge = (a: MapRect, b: MapRect) => {
  if (right(a) === b.x || right(b) === a.x)
    return Math.min(bottom(a), bottom(b)) > Math.max(a.y, b.y);
  if (bottom(a) === b.y || bottom(b) === a.y)
    return Math.min(right(a), right(b)) > Math.max(a.x, b.x);
  return false;
};

type DoorJoin =
  | { ok: true; space: string; to: string | null }
  | { ok: false; reason: "spans_two_walls" | "off_wall" | "inside_space" };

/** Mirrors brian `map_rules.door_joins`, per door instead of refusing the first bad one. */
export const doorJoin = (doc: MapDocument, door: MapDoor): DoorJoin => {
  const touches = doc.spaces.flatMap((space) =>
    space.boxes.map((box) => ({ space, t: touch(box, door.segment) })),
  );
  if (touches.some(({ t }) => t === "partial"))
    return { ok: false, reason: "spans_two_walls" };
  const full = touches
    .filter(({ t }) => t === "full")
    .map(({ space }) => space);
  if (full.length === 0) return { ok: false, reason: "off_wall" };
  if (new Set(full.map((s) => s.uuid)).size < full.length)
    return { ok: false, reason: "inside_space" };
  const [first, second] = full;
  return { ok: true, space: first!.uuid, to: second?.uuid ?? null };
};

export const joinsOf = (doc: MapDocument): ReadonlyMap<string, DoorJoin> =>
  new Map(doc.doors.map((door) => [door.uuid, doorJoin(doc, door)]));

/** The doors on the space's walls, to a neighbour or outside. */
export const doorsOf = (doc: MapDocument, space: MapSpace) =>
  doc.doors.filter((door) => {
    const join = doorJoin(doc, door);
    return join.ok && (join.space === space.uuid || join.to === space.uuid);
  });

type WallSide = "top" | "bottom" | "left" | "right";

/** Which side of one of the space's boxes a door on its walls is on, as the map draws it. */
export const wallSide = (door: MapDoor, space: MapSpace): WallSide | null => {
  const box = space.boxes.find((b) => touch(b, door.segment) === "full");
  if (!box) return null;
  const s = door.segment;
  if (horizontal(s)) return s.y1 === box.y ? "top" : "bottom";
  return s.x1 === box.x ? "left" : "right";
};

/** Where a door of `space` leads: the other space, or null for the outside. */
export const otherSide = (join: DoorJoin, space: MapSpace) =>
  join.ok ? (join.space === space.uuid ? join.to : join.space) : null;

/**
 * brian refuses an outside kind on a door between two spaces
 * (`outside_door_inside`), so a door that a box edit puts between two spaces
 * drops it. The same document comes back when nothing changes.
 */
export const withoutStaleOutside = (doc: MapDocument): MapDocument => {
  const stale = new Set(
    doc.doors
      .filter((door) => door.outside)
      .filter((door) => {
        const join = doorJoin(doc, door);
        return join.ok && join.to !== null;
      })
      .map((door) => door.uuid),
  );
  if (stale.size === 0) return doc;
  return {
    ...doc,
    doors: doc.doors.map((d) =>
      stale.has(d.uuid) ? { ...d, outside: null } : d,
    ),
  };
};

export const withoutDoor = (doc: MapDocument, uuid: string): MapDocument => ({
  ...doc,
  doors: doc.doors.filter((d) => d.uuid !== uuid),
});

interface Run {
  /** The grid line: a y for a horizontal run, an x for a vertical one. */
  line: number;
  horizontal: boolean;
  start: number;
  end: number;
}

type Edge = Run & { outward: -1 | 1 };

const toSegment = (run: Run): MapSegment =>
  run.horizontal
    ? { x1: run.start, y1: run.line, x2: run.end, y2: run.line }
    : { x1: run.line, y1: run.start, x2: run.line, y2: run.end };

const edges = (r: MapRect): Edge[] => [
  { line: r.y, horizontal: true, start: r.x, end: right(r), outward: -1 },
  { line: bottom(r), horizontal: true, start: r.x, end: right(r), outward: 1 },
  { line: r.x, horizontal: false, start: r.y, end: bottom(r), outward: -1 },
  { line: right(r), horizontal: false, start: r.y, end: bottom(r), outward: 1 },
];

/** The pieces of `edge` that `boxes` meet from the far side of its line. */
const covers = (edge: Edge, boxes: readonly MapRect[]): [number, number][] =>
  boxes.flatMap((box) => {
    const facing = edge.horizontal
      ? edge.outward === -1
        ? bottom(box)
        : box.y
      : edge.outward === -1
        ? right(box)
        : box.x;
    if (facing !== edge.line) return [];
    const [low, high] = edge.horizontal
      ? [box.x, right(box)]
      : [box.y, bottom(box)];
    const start = Math.max(edge.start, low);
    const end = Math.min(edge.end, high);
    return end > start ? [[start, end] as [number, number]] : [];
  });

/** `run` less the covered pieces. */
const subtract = (run: Run, covered: [number, number][]): Run[] => {
  const left: Run[] = [];
  let at = run.start;
  for (const [start, end] of [...covered].sort((a, b) => a[0] - b[0])) {
    if (start > at) left.push({ ...run, start: at, end: start });
    at = Math.max(at, end);
  }
  if (at < run.end) left.push({ ...run, start: at, end: run.end });
  return left;
};

const merge = (runs: Run[]): MapSegment[] => {
  const sorted = [...runs].sort(
    (a, b) =>
      Number(a.horizontal) - Number(b.horizontal) ||
      a.line - b.line ||
      a.start - b.start,
  );
  const merged: Run[] = [];
  for (const run of sorted) {
    const last = merged.at(-1);
    if (
      last &&
      last.horizontal === run.horizontal &&
      last.line === run.line &&
      last.end >= run.start
    )
      last.end = Math.max(last.end, run.end);
    else merged.push({ ...run });
  }
  return merged.map(toSegment);
};

/** The edges of `space`'s boxes that none of `others` meets. */
const uncovered = (space: MapSpace, others: readonly MapRect[]) =>
  merge(
    space.boxes.flatMap((box) =>
      edges(box).flatMap((edge) =>
        subtract(
          edge,
          covers(
            edge,
            others.filter((b) => b !== box),
          ),
        ),
      ),
    ),
  );

/**
 * The stretches of wall `space` shares with `other`, or, with `other` null, its
 * outer walls. A door anywhere on one joins the same two sides.
 */
export const wallStretches = (
  doc: MapDocument,
  space: MapSpace,
  other: MapSpace | null,
): MapSegment[] =>
  other
    ? merge(
        space.boxes.flatMap((box) =>
          edges(box).flatMap((edge) =>
            covers(edge, other.boxes).map(([start, end]) => ({
              ...edge,
              start,
              end,
            })),
          ),
        ),
      )
    : uncovered(
        space,
        doc.spaces.flatMap((s) => s.boxes),
      );

/** The space's outline: its boxes' edges less where two of its own boxes meet. */
export const outline = (space: MapSpace) => uncovered(space, space.boxes);

export const neighbours = (doc: MapDocument, space: MapSpace) =>
  doc.spaces.filter(
    (other) =>
      other.uuid !== space.uuid &&
      space.boxes.some((a) => other.boxes.some((b) => shareEdge(a, b))),
  );

const segmentsOverlap = (a: MapSegment, b: MapSegment) => {
  if (horizontal(a) !== horizontal(b)) return false;
  return horizontal(a)
    ? a.y1 === b.y1 && Math.min(a.x2, b.x2) > Math.max(a.x1, b.x1)
    : a.x1 === b.x1 && Math.min(a.y2, b.y2) > Math.max(a.y1, b.y1);
};

export const doorAt = (doc: MapDocument, segment: MapSegment) =>
  doc.doors.find((d) => segmentsOverlap(d.segment, segment)) ?? null;

/** The free one-cell spot on `stretch` nearest its middle; null when doors fill it. */
export const freeDoorCell = (
  doc: MapDocument,
  stretch: MapSegment,
): MapSegment | null => {
  const flat = horizontal(stretch);
  const length = flat ? stretch.x2 - stretch.x1 : stretch.y2 - stretch.y1;
  const middle = Math.floor((length - 1) / 2);
  const offsets = Array.from({ length }, (_, i) => i).sort(
    (a, b) => Math.abs(a - middle) - Math.abs(b - middle) || a - b,
  );
  for (const offset of offsets) {
    const cell = flat
      ? {
          x1: stretch.x1 + offset,
          y1: stretch.y1,
          x2: stretch.x1 + offset + 1,
          y2: stretch.y1,
        }
      : {
          x1: stretch.x1,
          y1: stretch.y1 + offset,
          x2: stretch.x1,
          y2: stretch.y1 + offset + 1,
        };
    if (!doorAt(doc, cell)) return cell;
  }
  return null;
};

export const onStretch = (door: MapSegment, stretch: MapSegment) =>
  horizontal(door) === horizontal(stretch) &&
  (horizontal(door)
    ? door.y1 === stretch.y1 && stretch.x1 <= door.x1 && door.x2 <= stretch.x2
    : door.x1 === stretch.x1 && stretch.y1 <= door.y1 && door.y2 <= stretch.y2);

/**
 * `door` moved `by` cells along its own line, kept on `stretch`: dragging a door
 * along its wall never takes it off the wall or round a corner.
 */
export const slide = (
  door: MapSegment,
  by: number,
  stretch: MapSegment,
): MapSegment => {
  if (horizontal(door)) {
    const length = door.x2 - door.x1;
    const x = Math.min(Math.max(door.x1 + by, stretch.x1), stretch.x2 - length);
    return { ...door, x1: x, x2: x + length };
  }
  const length = door.y2 - door.y1;
  const y = Math.min(Math.max(door.y1 + by, stretch.y1), stretch.y2 - length);
  return { ...door, y1: y, y2: y + length };
};

/** The stretch of wall the door sits on: shared with its other side, or outer. */
export const stretchOf = (
  doc: MapDocument,
  door: MapDoor,
): MapSegment | null => {
  const join = doorJoin(doc, door);
  if (!join.ok) return null;
  const space = doc.spaces.find((s) => s.uuid === join.space);
  const other = doc.spaces.find((s) => s.uuid === join.to) ?? null;
  if (!space) return null;
  return (
    wallStretches(doc, space, other).find((s) => onStretch(door.segment, s)) ??
    null
  );
};

/** The one-cell piece of grid line nearest `point`, if a door could go there. */
export const wallCellAt = (
  doc: MapDocument,
  point: GridPoint,
): MapSegment | null => {
  const across = { x: Math.round(point.x), y: Math.round(point.y) };
  const along = { x: Math.floor(point.x), y: Math.floor(point.y) };
  const candidates: MapSegment[] =
    Math.abs(point.y - across.y) <= Math.abs(point.x - across.x)
      ? [
          { x1: along.x, y1: across.y, x2: along.x + 1, y2: across.y },
          { x1: across.x, y1: along.y, x2: across.x, y2: along.y + 1 },
        ]
      : [
          { x1: across.x, y1: along.y, x2: across.x, y2: along.y + 1 },
          { x1: along.x, y1: across.y, x2: along.x + 1, y2: across.y },
        ];
  return (
    candidates.find(
      (segment) => doorJoin(doc, { uuid: "", segment, kind: "door" }).ok,
    ) ?? null
  );
};

/** The doors lying on the box's edges, which move with it. */
export const doorsOnBox = (doc: MapDocument, box: MapRect) =>
  doc.doors.filter((d) => touch(box, d.segment) === "full");

export const shifted = (s: MapSegment, dx: number, dy: number): MapSegment => ({
  x1: s.x1 + dx,
  y1: s.y1 + dy,
  x2: s.x2 + dx,
  y2: s.y2 + dy,
});

export const sameSegment = (a: MapSegment | null, b: MapSegment | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.x1 === b.x1 &&
    a.y1 === b.y1 &&
    a.x2 === b.x2 &&
    a.y2 === b.y2);

/** The box spanned by two grid points, at least one cell each way; null for a click. */
export const boxFromDrag = (a: GridPoint, b: GridPoint): MapRect | null => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(a.x - b.x);
  const h = Math.abs(a.y - b.y);
  return w >= 1 && h >= 1 ? { x, y, w, h } : null;
};

export const snap = (p: GridPoint, doc: MapDocument): GridPoint => ({
  x: Math.min(Math.max(Math.round(p.x), 0), doc.cols),
  y: Math.min(Math.max(Math.round(p.y), 0), doc.rows),
});

/** Devices sit on half cells. */
export const halfCell = (v: number) => Math.round(v * 2) / 2;

export const touchesSpace = (box: MapRect, space: MapSpace) =>
  space.boxes.some((b) => shareEdge(b, box));

export const overlapsAny = (doc: MapDocument, box: MapRect, except?: MapRect) =>
  doc.spaces.some((s) => s.boxes.some((b) => b !== except && overlap(b, box)));

export const centre = (r: MapRect): GridPoint => ({
  x: r.x + r.w / 2,
  y: r.y + r.h / 2,
});

export interface MapFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

const framed = (r: MapRect): MapFrame => ({
  x: r.x - 0.5,
  y: r.y - 0.5,
  width: r.w + 1,
  height: r.h + 1,
});

/** The canvas's view of the whole grid. */
export const frameOf = (doc: MapDocument): MapFrame =>
  framed({ x: 0, y: 0, w: doc.cols, h: doc.rows });

/**
 * The drawn spaces with the same margin; the whole grid until one is drawn.
 * Doors and devices need no room of their own: brian keeps them on a space's
 * wall (`door_off_wall`) and inside a space (`device_off_space`).
 */
export const drawnFrameOf = (doc: MapDocument): MapFrame => {
  const boxes = doc.spaces.flatMap((space) => space.boxes);
  if (boxes.length === 0) return frameOf(doc);
  const x = Math.min(...boxes.map((b) => b.x));
  const y = Math.min(...boxes.map((b) => b.y));
  return framed({
    x,
    y,
    w: Math.max(...boxes.map(right)) - x,
    h: Math.max(...boxes.map(bottom)) - y,
  });
};

/** The largest box of a space, which carries its label. */
export const labelBox = (space: MapSpace) =>
  space.boxes.reduce((big, b) => (b.w * b.h > big.w * big.h ? b : big));

/** brian refuses a device outside its space (`device_off_space`). */
export const insideSpace = (p: GridPoint, space: MapSpace) =>
  space.boxes.some(
    (r) => r.x <= p.x && p.x <= right(r) && r.y <= p.y && p.y <= bottom(r),
  );

/**
 * The space a screen may sit in at `p`: its home room's if it has one, else
 * any. A shared wall is in both spaces, so `current` wins while it holds `p`.
 */
export const screenSpaceAt = (
  doc: MapDocument,
  homeRoom: string | null,
  p: GridPoint,
  current: string,
) => {
  const allowed = doc.spaces.filter(
    (s) => (!homeRoom || s.room_uuid === homeRoom) && insideSpace(p, s),
  );
  return allowed.find((s) => s.uuid === current) ?? allowed[0];
};

/** Where a screen is first placed: its home room's space, else an area, else any space. */
export const screenHome = (doc: MapDocument, homeRoom: string | null) =>
  homeRoom
    ? doc.spaces.find((s) => s.room_uuid === homeRoom)
    : (doc.spaces.find((s) => s.area) ?? doc.spaces[0]);
